import React from 'react';
import PropTypes from 'prop-types';
import format from 'string-template';

import { PrimaryButton } from '../../../../../../components/Button/ActionButtons';
import { CheckIcon, CloseIcon, RefreshIcon } from '../icons';
import texts from '../texts';
import {
  FootBar,
  FootInner,
  Summary,
} from '../styles';

const Separator = () => <span className="sep">·</span>;

const renderSummary = (summary) => {
  const {
    total, saved, ready, uploading, needs, uploadFailed, saveFailed,
  } = summary;

  if (total === 0) {
    return <span className="s-muted">{texts.emptySummary}</span>;
  }

  const bits = [
    <span key="total">
      <b>{total}</b>
      {' '}
      {total === 1 ? texts.summaryExercise : texts.summaryExercises}
    </span>,
  ];
  if (saved) {
    bits.push(<span key="saved" className="s-saved">{format(texts.summarySaved, { count: saved })}</span>);
  }
  bits.push(<span key="ready" className="s-ready">{format(texts.summaryReady, { count: ready })}</span>);
  if (uploading) {
    bits.push(<span key="up" className="s-up">{format(texts.summaryUploading, { count: uploading })}</span>);
  }
  if (needs) {
    const template = needs === 1 ? texts.summaryNeed : texts.summaryNeedPlural;
    bits.push(<span key="need" className="s-need">{format(template, { count: needs })}</span>);
  }
  if (uploadFailed) {
    bits.push(
      <span key="upfail" className="s-fail">
        {format(texts.summaryUploadFailed, { count: uploadFailed })}
      </span>,
    );
  }
  if (saveFailed) {
    bits.push(<span key="savefail" className="s-fail">{format(texts.summarySaveFailed, { count: saveFailed })}</span>);
  }

  // Interleave separators between the summary bits, keyed off each bit's own key.
  return bits.flatMap((bit, position) => (
    position === 0 ? bit : [<Separator key={`sep-${bit.key}`} />, bit]
  ));
};

const Footer = ({
  summary,
  onSaveReady,
  onRetryFailed,
  onLeave,
  onDone,
  onCancel,
}) => {
  const {
    total, ready, saveFailed, unsaved,
  } = summary;

  let leftButton;
  if (total === 0) {
    leftButton = (
      <PrimaryButton variant="dimmed" icon={<CloseIcon />} onClick={onCancel}>
        {texts.cancel}
      </PrimaryButton>
    );
  } else if (unsaved > 0) {
    leftButton = (
      <PrimaryButton variant="dimmed" icon={<CloseIcon />} onClick={onLeave}>
        {texts.leave}
      </PrimaryButton>
    );
  } else {
    leftButton = (
      <PrimaryButton variant="dimmed" onClick={onDone}>
        {texts.doneViewLibrary}
      </PrimaryButton>
    );
  }

  let primaryButton;
  if (saveFailed > 0 && ready === 0) {
    primaryButton = (
      <PrimaryButton variant="danger" icon={<RefreshIcon />} onClick={onRetryFailed}>
        {format(texts.retryFailed, { count: saveFailed })}
      </PrimaryButton>
    );
  } else {
    // The batch action also retries save-failed cards, so in a mixed state the
    // count reflects everything the click will process.
    const saveCount = ready + saveFailed;
    primaryButton = (
      <PrimaryButton icon={<CheckIcon />} disabled={saveCount === 0} onClick={onSaveReady}>
        {format(texts.saveReady, { count: saveCount })}
      </PrimaryButton>
    );
  }

  return (
    <FootBar>
      <FootInner>
        <Summary>{renderSummary(summary)}</Summary>
        {leftButton}
        {primaryButton}
      </FootInner>
    </FootBar>
  );
};

Footer.propTypes = {
  summary: PropTypes.shape({
    total: PropTypes.number,
    saved: PropTypes.number,
    ready: PropTypes.number,
    uploading: PropTypes.number,
    needs: PropTypes.number,
    uploadFailed: PropTypes.number,
    saveFailed: PropTypes.number,
    unsaved: PropTypes.number,
  }).isRequired,
  onSaveReady: PropTypes.func.isRequired,
  onRetryFailed: PropTypes.func.isRequired,
  onLeave: PropTypes.func.isRequired,
  onDone: PropTypes.func.isRequired,
  onCancel: PropTypes.func.isRequired,
};

export default Footer;
